import { useState, useRef } from "react";
import { ChevronDown } from "lucide-react";
import { IbicDropdown } from "../IbicDropdown/IbicDropdown";
import { useRecentlyUsedIbic } from "../../hooks/useRecentlyUsedIbic";
import {
    translateToSwedish,
    findIbicCategoryKey
} from "../../constants/ibicCategories";
import styles from "./IbicInlineSelector.module.css";

interface IbicInlineSelectorProps {
    label: string;
    value: string;
    onChange: (newValue: string) => void;
    type: "category" | "code";
    language?: "en" | "sv";
    disabled?: boolean;
}

export const IbicInlineSelector = ({
    label,
    value,
    onChange,
    type,
    language = "sv",
    disabled = false
}: IbicInlineSelectorProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLSpanElement>(null);
    const { addRecent } = useRecentlyUsedIbic();

    const handleSelect = (selectedCategory: string) => {
        addRecent(selectedCategory);
        // Translate to the appropriate language for display
        const displayValue = language === "sv"
            ? translateToSwedish(selectedCategory)
            : selectedCategory;
        onChange(displayValue);
        setIsOpen(false);
    };

    const handleClick = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    // Get the English key for the current value to pass to dropdown
    const englishKey = findIbicCategoryKey(value);

    // Format display value - for codes, we might have format like "d550 (Äta)"
    // We want to display nicely
    const displayValue = value || "-";

    return (
        <span className={styles.inlineSelector}>
            <span className={styles.label}>{label}:</span>
            <span className={styles.dropdownWrapper}>
                <span
                    ref={triggerRef}
                    className={`${styles.value} ${disabled ? styles.disabled : ""}`}
                    onClick={handleClick}
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleClick();
                        }
                    }}
                >
                    <span className={styles.valueText}>{displayValue}</span>
                    {!disabled && <ChevronDown size={14} className={styles.chevron} />}
                </span>
                <IbicDropdown
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    onSelect={handleSelect}
                    selectedValue={englishKey}
                    triggerRef={triggerRef}
                    filterType={type}
                />
            </span>
        </span>
    );
};

export default IbicInlineSelector;
